import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiChevronUp,
  FiUser,
  FiShield,
  FiMoon,
  FiSun,
  FiLogOut
} from 'react-icons/fi';
import { authService } from '../services/auth';
import { useTheme } from '../contexts/ThemeContext';
import './UserMenu.css';

interface UserMenuProps {
  /** Rota base do app onde a página de perfil vive (ex.: "/dashboard" ou "/admin") */
  profileBase: string;
  /** Rótulo descritivo do papel (Administrador, Cliente, etc) */
  roleLabel: string;
}

const UserMenu: React.FC<UserMenuProps> = ({ profileBase, roleLabel }) => {
  const user = authService.getUser();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        close();
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, close]);

  const handleLogout = () => {
    close();
    authService.logout();
  };

  const initial = (user?.name || 'U').charAt(0).toUpperCase();
  const isDark = theme === 'dark';

  return (
    <div className={`user-menu ${open ? 'is-open' : ''}`} ref={ref}>
      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-avatar">{initial}</div>
            <div className="user-menu-identity">
              <span className="user-menu-name">{user?.name || 'Usuário'}</span>
              <span className="user-menu-email">{user?.email || ''}</span>
            </div>
          </div>

          <div className="user-menu-divider" />

          <Link
            to={`${profileBase}/profile`}
            className="user-menu-item"
            role="menuitem"
            onClick={close}
          >
            <FiUser />
            <span>Meu perfil</span>
          </Link>

          <Link
            to={`${profileBase}/profile#twofa`}
            className="user-menu-item"
            role="menuitem"
            onClick={close}
          >
            <FiShield />
            <span>Segurança &amp; 2FA</span>
          </Link>

          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              toggleTheme();
            }}
          >
            {isDark ? <FiSun /> : <FiMoon />}
            <span>{isDark ? 'Tema claro' : 'Tema escuro'}</span>
          </button>

          <div className="user-menu-divider" />

          <button
            type="button"
            className="user-menu-item danger"
            role="menuitem"
            onClick={handleLogout}
          >
            <FiLogOut />
            <span>Sair</span>
          </button>
        </div>
      )}

      <button
        type="button"
        className="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="user-menu-trigger-avatar">{initial}</div>
        <div className="user-menu-trigger-meta">
          <span className="user-menu-trigger-name">{user?.name || 'Usuário'}</span>
          <span className="user-menu-trigger-role">{roleLabel}</span>
        </div>
        <FiChevronUp className="user-menu-trigger-chevron" />
      </button>
    </div>
  );
};

export default UserMenu;
